import React, { useEffect, useState } from 'react';
import clsx from 'clsx';

import { MEDIA_TYPES } from '../../../../constants';
import CommonLoader from '../../../../components/common-loader/CommonLoader';
import useStoryMediaDisplay from '../../hooks/useStoryMediaDisplay';
import BackgroundImage from '../../../components/content-media/background-image/BackgroundImage';

import scss from './StoryMediaView.module.scss';

function StoryMediaView({ media, variant = 'card', className, imageClassName, videoClassName, onImageLoad }) {
  const { type, link, poster, cardSrc, isVideo, isLoading, isDeleted } = useStoryMediaDisplay(media);
  const [fallbackBackground, setFallbackBackground] = useState(null);

  useEffect(() => {
    setFallbackBackground(null);
  }, [link, cardSrc]);

  if (isDeleted) {
    return <div className={clsx(scss.placeholder, className)} />;
  }

  if (isLoading) {
    return (
      <div className={clsx(scss.loaderWrapper, className)}>
        <CommonLoader isLoading width={variant === 'card' ? 24 : 32} strokeWidth='3' />
      </div>
    );
  }

  if (variant === 'card') {
    if (isVideo && link && !cardSrc) {
      return (
        <video
          src={link}
          className={clsx(scss.cardMedia, className)}
          muted
          playsInline
          preload='metadata'
          draggable={false}
        />
      );
    }

    if (!cardSrc) {
      return <div className={clsx(scss.placeholder, className)} />;
    }

    return <img src={cardSrc} alt='' className={clsx(scss.cardMedia, className)} draggable={false} />;
  }

  if (variant === 'preview') {
    if (!link) {
      return <div className={clsx(scss.placeholder, className)} />;
    }

    if (type === MEDIA_TYPES.IMAGE) {
      return <img src={link} alt='' className={clsx(scss.previewMedia, className, imageClassName)} />;
    }

    return (
      <video
        src={link}
        poster={poster || undefined}
        className={clsx(scss.previewMedia, className, videoClassName)}
        controls
        playsInline>
        <track kind='captions' />
      </video>
    );
  }

  if (variant === 'hero') {
    if (!link) {
      return <div className={clsx(scss.placeholder, className)} />;
    }

    if (type === MEDIA_TYPES.IMAGE) {
      const backgroundImage = link || fallbackBackground;

      return (
        <>
          <BackgroundImage src={backgroundImage} alt='' className={scss.backgroundImage} />
          <img
            src={link}
            alt={media?.fileName || ''}
            className={clsx(scss.heroMedia, className, imageClassName)}
            onLoad={(event) => {
              if (!fallbackBackground) {
                setFallbackBackground(event.currentTarget.src);
              }

              onImageLoad?.(event);
            }}
          />
        </>
      );
    }

    return (
      <video
        src={link}
        poster={poster || undefined}
        className={clsx(scss.heroMedia, className, videoClassName)}
        controls
        playsInline>
        <track kind='captions' />
      </video>
    );
  }

  return null;
}

export default StoryMediaView;
